import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FormField, FormFieldLabel, FormFieldValue } from 'styles/common.styles';
import { theme } from 'styles/theme';
import {
  toDisplayDate,
  toStorageDate,
  todayStorage,
  isDisabledDate,
  validateDateOrder,
} from 'utils/datePickerUtils';
import DateCalendarPopover from './DateCalendarPopover';

const InputWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: ${(p) => p.$width || 'auto'};
  flex: 0 0 auto;
`;

const CalBtn = styled.button`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 2px;
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  z-index: 1;
  &:hover:not(:disabled) {
    color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight || 'rgba(37,99,235,0.08)'};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const DateInput = styled(FormFieldValue)`
  padding-right: 26px;
`;

/**
 * DateField (M2 단일)
 * - 직접 입력(YYYY-MM-DD) + 캘린더 팝오버
 * - 저장값: YYYYMMDD (기존 FormArea 호환)
 * - pairEndValue / onPairInvalid: M3 교차검증용
 */
const DateField = ({
  name,
  label,
  value = '',
  width,
  labelWidth,
  fieldWidth,
  showColon = false,
  readonly = false,
  min,
  max,
  showTodayShortcut = true,
  showPresets = false,
  presetSet = 'inquiry',
  fiscalYear,
  anchorFrom,
  yearPreset = 'calendar',
  pairEndValue,
  pairStartValue,
  isPairEnd = false,
  allowEmptyEnd = true,
  onPairInvalid,
  onChange,
  onBlur,
  fieldProps = {},
}) => {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [text, setText] = useState(toDisplayDate(value));

  useEffect(() => {
    setText(toDisplayDate(value));
  }, [value]);

  const emit = (storageOrEmpty) => {
    if (!onChange) return;
    onChange({
      target: { name, value: storageOrEmpty || '' },
    });
  };

  const openPicker = () => {
    if (readonly) return;
    const rect = wrapRef.current?.getBoundingClientRect?.();
    if (rect) {
      setAnchor({ top: rect.top, left: rect.left, bottom: rect.bottom, right: rect.right });
    }
    setOpen(true);
  };

  const commitText = (raw) => {
    const trimmed = String(raw || '').trim();
    if (!trimmed) {
      emit('');
      setText('');
      return;
    }
    const stor = toStorageDate(trimmed);
    if (!stor || stor.length !== 8) {
      setText(toDisplayDate(value));
      return;
    }
    if (isDisabledDate(stor, min, max)) {
      setText(toDisplayDate(value));
      return;
    }
    // M3: end field vs start
    if (isPairEnd && pairStartValue) {
      const v = validateDateOrder(pairStartValue, stor, { allowEmptyEnd });
      if (!v.ok) {
        onPairInvalid?.(v.reason);
        setText(toDisplayDate(value));
        return;
      }
    }
    if (!isPairEnd && pairEndValue) {
      const v = validateDateOrder(stor, pairEndValue, { allowEmptyEnd });
      if (!v.ok) {
        onPairInvalid?.(v.reason);
        setText(toDisplayDate(value));
        return;
      }
    }
    emit(stor);
    setText(toDisplayDate(stor));
  };

  const handleBlur = (e) => {
    commitText(text);
    onBlur?.(e);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitText(text);
    }
    if (e.key === 'ArrowDown' && (e.altKey || e.ctrlKey)) {
      e.preventDefault();
      openPicker();
    }
    fieldProps.onKeyDown?.(e);
  };

  return (
    <FormField width={fieldWidth || 'auto'}>
      {label && (
        <FormFieldLabel width={labelWidth}>{showColon ? `${label}:` : label}</FormFieldLabel>
      )}
      <InputWrap ref={wrapRef} $width={width || '120px'}>
        <DateInput
          type="text"
          name={name}
          inputMode="numeric"
          autoComplete="off"
          placeholder="YYYY-MM-DD"
          title="직접 입력 또는 캘린더에서 선택 (형식: YYYY-MM-DD)"
          value={text}
          readOnly={readonly}
          disabled={readonly}
          onChange={
            readonly
              ? undefined
              : (e) => {
                  setText(e.target.value);
                }
          }
          onBlur={readonly ? undefined : handleBlur}
          onKeyDown={readonly ? undefined : handleKeyDown}
          width="100%"
          {...fieldProps}
        />
        <CalBtn
          type="button"
          tabIndex={-1}
          disabled={readonly}
          title="캘린더 열기"
          aria-label="캘린더 열기"
          onMouseDown={(e) => e.preventDefault()}
          onClick={openPicker}
        >
          ▤
        </CalBtn>
      </InputWrap>
      {open && (
        <DateCalendarPopover
          mode="single"
          value={toStorageDate(value) || todayStorage()}
          min={min}
          max={max}
          showTodayShortcut={showTodayShortcut}
          showPresets={showPresets}
          presetSet={presetSet}
          fiscalYear={fiscalYear}
          anchorFrom={anchorFrom}
          yearPreset={yearPreset}
          anchorRect={anchor}
          onClose={() => setOpen(false)}
          onSelect={(stor) => {
            if (isPairEnd && pairStartValue) {
              const v = validateDateOrder(pairStartValue, stor, { allowEmptyEnd });
              if (!v.ok) {
                onPairInvalid?.(v.reason);
                return;
              }
            }
            if (!isPairEnd && pairEndValue) {
              const v = validateDateOrder(stor, pairEndValue, { allowEmptyEnd });
              if (!v.ok) {
                onPairInvalid?.(v.reason);
                return;
              }
            }
            emit(stor);
            setText(toDisplayDate(stor));
            setOpen(false);
          }}
        />
      )}
    </FormField>
  );
};

export default DateField;
